import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { SpouseSwitcher, type SpouseInfo } from './SpouseSwitcher';
import { useFamilyStore, useHybridPedigreeStore, useDomainStore } from '@/hooks/useStores';
import appConfig from '@/config/app';
import { calendarYearFromStoredDate } from '@/utils/dateFormatting';
import Target from '@/icons/Target';
import UserIcon from '@/icons/User';
import Plus from '@/icons/Plus';
import './person-card.css';

function nameInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join('')
      .toUpperCase() || '?'
  );
}

function resolvePhotoSrc(photoUrl: string | null | undefined): string | null {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('http')) return photoUrl;
  const origin = appConfig.apiUrl.replace(/\/v2\/?$/, '');
  return `${origin}${photoUrl}`;
}

interface AddMemberDropdownWithTooltipProps {
  onAddChildSelect: () => void;
  onAddSpouseSelect: () => void;
  onAddPetSelect: () => void;
}

function AddMemberDropdownWithTooltip({
  onAddChildSelect,
  onAddSpouseSelect,
  onAddPetSelect
}: AddMemberDropdownWithTooltipProps) {
  const { t } = useTranslation('forms');
  const addTriggerButton = (
    <button
      type="button"
      className="person-card-action person-card-action--add"
      aria-label={t('personCard.addFamilyMember')}
      aria-haspopup="menu"
      title={t('personCard.addFamilyMember')}
    >
      <Plus width={14} height={14} />
    </button>
  );

  return (
    <div className="person-card-action-wrap person-card-action-wrap--add" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>{addTriggerButton}</DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="person-card-add-dropdown"
            side="top"
            align="end"
            sideOffset={4}
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu.Item className="person-card-add-dropdown__item" onSelect={onAddChildSelect}>
              {t('personCard.addChild')}
            </DropdownMenu.Item>
            <DropdownMenu.Item className="person-card-add-dropdown__item" onSelect={onAddSpouseSelect}>
              {t('personCard.addSpouse')}
            </DropdownMenu.Item>
            <DropdownMenu.Item className="person-card-add-dropdown__item" onSelect={onAddPetSelect}>
              {t('personCard.addPet')}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

interface PersonCardProps {
  familyMemberId: string;
  generation: number;
  onTreeViewClick?: (familyMemberId: string) => void;
  onProfileClick?: (familyMemberId: string) => void;
  onAddClick?: (familyMemberId: string, option: 'child' | 'spouse' | 'pet') => void;
  canAddFamilyMember?: boolean;
  isTreeAnchor: boolean;
  useGrayBorder?: boolean;
  /** Members list layout: design v0.1 card styling and icon actions */
  layout?: 'default' | 'members';
}

export const PersonCard: React.FC<PersonCardProps> = observer(
  ({
    familyMemberId,
    generation,
    onTreeViewClick,
    onProfileClick,
    onAddClick,
    canAddFamilyMember = false,
    isTreeAnchor,
    useGrayBorder = false,
    layout = 'default'
  }) => {
    const isMembersLayout = layout === 'members';
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const familyStore = useFamilyStore();
    const hybridPedigreeStore = useHybridPedigreeStore();
    const domainStore = useDomainStore();
    const thisPerson = familyStore.getPerson(familyMemberId);

    // Check if this person is claimed by the current user
    const isClaimedByCurrentUser =
      thisPerson?.userId && domainStore.user?.id && thisPerson.userId === domainStore.user.id;

    // Graceful fallback for missing persons (e.g., during family switching or data loading)
    if (!thisPerson) {
      // eslint-disable-next-line no-console
      console.warn(`Family member ${familyMemberId} not found in FamilyStore when rendering PersonCard`);
      return (
        <div className="person-card person-card-error">
          <div>{t('personCard.personNotFound')}</div>
          <div className="person-card-error-id">{familyMemberId}</div>
        </div>
      );
    }

    const isPlaceholder = thisPerson.isPlaceholder;
    const isDeceased =
      thisPerson.livingStatus === 'deceased' || (thisPerson.deathDate != null && thisPerson.deathDate !== undefined);

    // Filter out missing spouses gracefully
    const spouses: SpouseInfo[] = thisPerson.spouseFamilyMemberIds
      .map((spouseFamilyMemberId): SpouseInfo | null => {
        const spouse = familyStore.getPerson(spouseFamilyMemberId);
        if (!spouse) {
          // eslint-disable-next-line no-console
          console.warn(`Spouse ${spouseFamilyMemberId} not found in FamilyStore when rendering PersonCard`);
          return null;
        }
        return {
          id: spouse.familyMemberId, // Use familyMemberId for consistency
          displayName: spouse.fullName
        };
      })
      .filter((spouse): spouse is SpouseInfo => spouse !== null);

    const age = thisPerson.age;
    const birthYear = calendarYearFromStoredDate(thisPerson.birthDate);
    const deathYear = calendarYearFromStoredDate(thisPerson.deathDate);

    // Determine if this card should be clickable
    // Placeholders are only clickable if they have known (non-placeholder) children
    // Parent/grandparent cards are not clickable when TreeAnchor is a placeholder
    const hasKnownChildren = familyStore.hasKnownChildren(familyMemberId);
    const isClickable =
      (!isPlaceholder || hasKnownChildren) &&
      !(generation < 0 && hybridPedigreeStore.isTreeAnchorPlaceholder && isPlaceholder && !hasKnownChildren);

    // Card is no longer clickable - only action buttons are interactive

    const handleTreeViewClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      if (onTreeViewClick) {
        onTreeViewClick(familyMemberId);
      }
    };

    const handleProfileClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      if (onProfileClick) {
        onProfileClick(familyMemberId);
      }
    };

    const handleAddChildSelect = () => {
      if (onAddClick) {
        onAddClick(familyMemberId, 'child');
      }
    };

    const handleAddSpouseSelect = () => {
      if (onAddClick) {
        onAddClick(familyMemberId, 'spouse');
      }
    };

    const handleAddPetSelect = () => {
      if (onAddClick) {
        onAddClick(familyMemberId, 'pet');
      }
    };

    // Handle spouse selection
    const handleSpouseSelect = (spouseFamilyMemberId: string) => {
      // Update the person model with the selected spouse
      thisPerson.selectSpouse(spouseFamilyMemberId);

      // Update tree visualization based on generation
      if (generation === 0) {
        // Root generation: Update root generation
        // Handles two cases:
        // 1. TreeAnchor selects a different spouse: Updates root generation, keeps same TreeAnchor
        // 2. LeftRootFamilyMember (non-TreeAnchor) selects a different spouse: Changes TreeAnchor to newly selected spouse, regenerates tree
        hybridPedigreeStore.updateRootGenerationForSpouseSelection(familyMemberId);
      } else if (generation === -1) {
        // Parents generation: Recalculate parents generation and update grandparents generation
        hybridPedigreeStore.updateGrandparentsGenerationForSpouseSelection(familyMemberId);
      } else if (generation === -2) {
        // Grandparents generation: Update the grandparent position to show newly selected spouse
        hybridPedigreeStore.updateGrandparentsGenerationForGrandparentSpouseSelection(familyMemberId);
      }
    };

    // Determine CSS classes based on generation and state
    const getCardClasses = () => {
      const classes = ['person-card'];

      if (isMembersLayout) {
        classes.push('person-card--members-list');
      }

      if (!isClickable) {
        classes.push('person-card--not-clickable');
      }

      if (isMembersLayout) {
        // Members list uses unified forest border from CSS
      } else if (useGrayBorder) {
        classes.push('person-card--gray-border');
      } else if (isTreeAnchor) {
        classes.push('person-card--tree-anchor');
      } else {
        switch (generation) {
          case 0:
            classes.push('person-card--generation-0');
            break;
          case -1:
          case -2:
            classes.push('person-card--generation-parents');
            break;
          case 1:
            classes.push('person-card--generation-children');
            break;
        }
      }

      if (isDeceased) {
        classes.push('person-card--deceased');
      }

      return classes.join(' ');
    };

    // Show action menu if we have tree view, profile, or add handlers
    const showActionMenu = (onTreeViewClick || onProfileClick || (canAddFamilyMember && onAddClick)) && isClickable;

    return (
      <div className={getCardClasses()} data-family-member-id={familyMemberId}>
        {/* Name and star indicator container */}
        <div className="person-card-name-container">
          <div className={`person-card-name ${isDeceased ? 'person-card-name--deceased' : ''}`}>
            {thisPerson.fullName}
          </div>
          {/* Visual indicator for user's own profile */}
          {isClaimedByCurrentUser &&
            (isMembersLayout ? (
              <span className="person-card-you-badge">{t('personCard.youBadge')}</span>
            ) : (
              <div className="person-card-indicator" title={t('personCard.yourProfileTitle')}>
                ⭐
              </div>
            ))}
        </div>

        {/* Profile picture and metadata container */}
        <div className="person-card-body">
          {/* Profile picture or placeholder - left */}
          <div className="person-card-photo">
            {resolvePhotoSrc(thisPerson.photoUrl) ? (
              <img src={resolvePhotoSrc(thisPerson.photoUrl)!} alt="" className="person-card-photo-img" />
            ) : (
              <div className="person-card-photo-placeholder">
                {isMembersLayout ? (
                  <span className="person-card-photo-initials">{nameInitials(thisPerson.fullName)}</span>
                ) : (
                  <span className="person-card-photo-icon">📷</span>
                )}
              </div>
            )}
          </div>

          {/* Person metadata - right of photo, stacked */}
          <div className="person-card-metadata">
            {/* Birth and Death Years - only show for non-placeholders */}
            {!isPlaceholder && birthYear && (
              <div className="person-card-years">
                {isDeceased && deathYear
                  ? tCommon('genealogy.bornYearRange', { birth: birthYear, death: deathYear })
                  : tCommon('genealogy.bornYear', { year: birthYear })}
              </div>
            )}

            {/* Age - only show for non-placeholders */}
            {!isPlaceholder && age !== null && <div className="person-card-age">({age})</div>}
            {isMembersLayout && !isPlaceholder && (
              <div className="person-card-place">{thisPerson.birthPlace?.trim() ?? ''}</div>
            )}
          </div>
        </div>

        {/* Bottom row: Spouse switcher (left) + Action buttons (right) - matches TreePersonCard layout */}
        {(showActionMenu || (!isMembersLayout && !isPlaceholder && generation <= 0 && spouses.length > 1)) && (
          <div className="person-card-bottom-row" onClick={(e) => e.stopPropagation()}>
            {/* Spouse switcher - tree view only (not members list) */}
            {!isMembersLayout && !isPlaceholder && generation <= 0 && spouses.length > 1 && (
              <SpouseSwitcher
                familyMemberId={familyMemberId}
                spouses={spouses}
                currentSpouseFamilyMemberId={thisPerson.selectedSpouseFamilyMemberId}
                onSpouseSelect={handleSpouseSelect}
              />
            )}
            {/* Action buttons - right side */}
            {showActionMenu && (
              <div className="person-card-actions">
                {canAddFamilyMember && onAddClick && (
                  <AddMemberDropdownWithTooltip
                    onAddChildSelect={handleAddChildSelect}
                    onAddSpouseSelect={handleAddSpouseSelect}
                    onAddPetSelect={handleAddPetSelect}
                  />
                )}
                {onTreeViewClick && (
                  <button
                    type="button"
                    className="person-card-action person-card-action--anchor"
                    onClick={handleTreeViewClick}
                    aria-label={t('personCard.viewTreeAnchored')}
                    title={t('personCard.viewTreeAnchored')}
                  >
                    <Target width={14} height={14} />
                  </button>
                )}
                {onProfileClick && (
                  <button
                    type="button"
                    className="person-card-action person-card-action--profile"
                    onClick={handleProfileClick}
                    aria-label={t('personCard.openProfile')}
                    title={t('personCard.openProfile')}
                  >
                    <UserIcon width={14} height={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
